import {
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import moment from 'moment-timezone'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IoIosCheckmarkCircleOutline } from 'react-icons/io'
import { useNudgePeerAssessmentMutation } from '../../../app/services/api'

type RespondentType = 'peer' | 'manager' | 'reportee'
type RespondentStatus = 'pending' | 'completed' | 'started'

export interface RespondentData {
  type: RespondentType
  email: string
  lastRequested: Date
  requestedCount: number
  status: RespondentStatus
}

interface RespondentGroupData {
  type: RespondentType
  labelSingular: string
  labelPlural: string
  respondents: RespondentData[]
  onEditRespondent: (respondent: any) => void
  nonEditable?: boolean
}

export const RespondentGroup = (data: RespondentGroupData) => {
  return (
    <>
      <Heading as="h6" size="sm" mb={4}>
        {data.respondents.length === 1 ? data.labelSingular : data.labelPlural}{' '}
        (
        {
          data.respondents.filter(
            (respondent) => respondent.status === 'completed'
          ).length
        }
        /{data.respondents.length})
      </Heading>
      {data.respondents.map((respondent) => (
        <Respondent
          key={respondent.email}
          type={data.type}
          data={respondent}
          onEditRespondent={data.onEditRespondent}
          nonEditable={data.nonEditable}
        />
      ))}
      <Spacer mt={8} />
    </>
  )
}

const Respondent = ({
  type,
  data,
  onEditRespondent,
  nonEditable,
}: {
  type: RespondentType
  data: RespondentData
  onEditRespondent: (respondent: any) => void
  nonEditable?: boolean
}) => {
  const [nudgePeer] = useNudgePeerAssessmentMutation()
  const toast = useToast()
  const { t } = useTranslation()

  const [isConfirmAlertOpen, setIsConfirmAlertOpen] = useState(false)

  const onNudge = useCallback(async () => {
    console.log('onNudge')
    await nudgePeer({ email: data.email })
    toast({
      status: 'success',
      title: t('success'),
      description: t('nudge_successful'),
      isClosable: true,
    })
    setIsConfirmAlertOpen(false)
  }, [data.email, nudgePeer, toast])
  return (
    <Flex
      bg={data.status === 'completed' ? '#ebfff0' : '#fff'}
      border={`1px solid`}
      borderColor={data.status === 'completed' ? '#14752e' : '#dcc'}
      borderRadius="md"
      mb={'2'}
      pb="0px"
    >
      <HStack spacing="24px" width={'100%'}>
        <Box
          textAlign={{
            base: 'center',
            sm: 'center',
            md: 'center',
            lg: 'center',
          }}
          verticalAlign="center"
          pl="5"
          pt="5"
          flexGrow={0}
        >
          <Avatar
            size={'lg'}
            mb={4}
            borderStyle="solid"
            borderWidth={'1px'}
            borderColor={'GrayText'}
            pos={'relative'}
          />
        </Box>
        <VStack align="stretch" spacing={0} width={'80%'}>
          <Heading pb={1} mb="0" as="h6" size="sm">
            {data.email}
          </Heading>
          {data.status === 'completed' ? (
            <Text mt={0} mb={0} fontSize="sm">
              {t('status')}: <i>{t('completed')}</i>
            </Text>
          ) : (
            <>
              <Text mt={0} mb={0} fontSize="sm">
                {t('last_requested')}:{' '}
                <i>{moment(data.lastRequested).format('MMMM DD')}</i>
              </Text>
              <Text mt={0} mb={0} fontSize="sm">
                {t('requests_sent')}: <i>{data.requestedCount}</i>
              </Text>
            </>
          )}
        </VStack>
        <Center width={'80%'} justifyContent={'flex-end'}>
          <Box p="5" flexDir={'row'}>
            {data.status === 'completed' ? (
              <IoIosCheckmarkCircleOutline size={40} />
            ) : !nonEditable ? (
              <>
                <Button
                  onClick={() => onEditRespondent({ email: data.email, type })}
                  variant="outline"
                  mx={2}
                >
                  {t('edit')}
                </Button>
                <Button
                  onClick={() => setIsConfirmAlertOpen(true)}
                  colorScheme="primary"
                  variant="outline"
                >
                  {t('resend')}
                </Button>
              </>
            ) : null}
          </Box>
        </Center>
      </HStack>
      <Modal
        isOpen={isConfirmAlertOpen}
        onClose={() => setIsConfirmAlertOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader> {t('resend_invitation')} </ModalHeader>
          <Divider />
          <ModalBody>
            <Text fontSize="18px">
              {t('resend_request_confirmation', {
                email: data.email,
              })}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsConfirmAlertOpen(false)} mr={5}>
              {t('cancel')}
            </Button>
            <Button onClick={onNudge} colorScheme="primary" type="submit">
              {t('resend')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  )
}
